import type { Client, Contact, CrmData, CrmUser, Project, Task, UserFilter, UserRole } from '../types/crm';
import { canViewAllAccounts } from '../lib/accountVisibility';
import { canViewAssignedTeam } from '../lib/roleAccess';
import {
  detectOwnerIdKind,
  ownerIdForUser,
  ownerLabelForRef,
  ownerMatchesFilter,
  type OwnerIdKind,
} from './ownerId';
import {
  clientMatchesSalespersonCodes,
  formatClientSalespersonCode,
  normalizeSalespersonCodes,
} from './salespersonScope';

export interface OwnerOption {
  /** Value stored in owner_id column (auth_id or username). */
  value: string;
  display_name: string;
  username: string;
  userId: string;
}

export type SalespersonCodesByUsername = Record<string, string[]>;

export function ownerOptionMatches(option: OwnerOption, ref: string): boolean {
  return option.value === ref || option.userId === ref || option.username === ref;
}

/** Valid FK targets — includes signed-in user if team list did not sync. */
export function collectAssignableOwners(users: CrmUser[], kind: OwnerIdKind): OwnerOption[] {
  const seen = new Set<string>();
  const options: OwnerOption[] = [];

  for (const u of users) {
    if (!u.id || !u.username || seen.has(u.id)) continue;
    seen.add(u.id);
    options.push({
      value: ownerIdForUser(u, kind),
      display_name: u.display_name?.trim() || u.username,
      username: u.username,
      userId: u.id,
    });
  }

  return options.sort((a, b) =>
    a.display_name.localeCompare(b.display_name, undefined, { sensitivity: 'base' }),
  );
}

export function buildOwnerLabelMap(users: CrmUser[], data: CrmData): Map<string, string> {
  const map = new Map<string, string>();
  for (const u of users) {
    map.set(u.id, u.display_name?.trim() || u.username);
    map.set(u.username, u.display_name?.trim() || u.username);
  }
  for (const ref of collectAllOwnerRefs(data)) {
    if (!map.has(ref)) {
      map.set(ref, ownerLabelForRef(users, ref));
    }
  }
  return map;
}

function collectAllOwnerRefs(data: CrmData): string[] {
  const ids = new Set<string>();
  const add = (id?: string) => {
    if (id?.trim()) ids.add(id.trim());
  };
  // clients.owner_id is salesperson code — do not treat as person labels.
  for (const c of data.contacts) add(c.owner_id);
  for (const p of data.projects) add(p.owner_id);
  for (const t of data.tasks) add(t.owner_id);
  return Array.from(ids);
}

export function displayNameForUser(users: CrmUser[], username?: string, labelMap?: Map<string, string>): string {
  if (!username) return '—';
  if (labelMap?.has(username)) return labelMap.get(username)!;
  return ownerLabelForRef(users, username);
}

/** Compare DB ids that may be bigint or uuid serialized as string/number. */
export function idsMatch(a?: string | number | null, b?: string | number | null): boolean {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

export function clientById(clients: Client[], id?: string | null): Client | undefined {
  if (!id) return undefined;
  return clients.find((c) => idsMatch(c.id, id));
}

export function contactsForClient(contacts: Contact[], clientId?: string | null): Contact[] {
  if (!clientId) return [];
  return contacts.filter((c) => idsMatch(c.client_id, clientId));
}

export function contactLabel(contact: Contact): string {
  return `${contact.first_name} ${contact.last_name}`.trim();
}

type DataKey = keyof Pick<CrmData, 'clients' | 'contacts' | 'projects' | 'tasks'>;

function ownerInAllowedSet(
  ownerId: string | undefined,
  username: string,
  users: CrmUser[],
  teamUsernames: string[],
): boolean {
  if (!ownerId) return false;
  const allowed = new Set(
    [username, ...teamUsernames].map((u) => u.trim().toLowerCase()).filter(Boolean),
  );
  if (allowed.has(ownerId.trim().toLowerCase())) return true;
  const ownerUser = users.find(
    (u) =>
      u.id === ownerId ||
      u.username === ownerId ||
      u.username.trim().toLowerCase() === ownerId.trim().toLowerCase(),
  );
  return ownerUser != null && allowed.has(ownerUser.username.trim().toLowerCase());
}

function codesForUserFilter(
  userFilter: UserFilter,
  _username: string,
  actorCodes: string[],
  codesByUsername: SalespersonCodesByUsername,
): string[] {
  if (userFilter === 'All') return [];
  if (userFilter === 'Me') return normalizeSalespersonCodes(actorCodes);
  const key = userFilter.trim().toLowerCase();
  const fromMap =
    codesByUsername[userFilter] ??
    codesByUsername[key] ??
    Object.entries(codesByUsername).find(([u]) => u.trim().toLowerCase() === key)?.[1];
  return normalizeSalespersonCodes(fromMap ?? []);
}

function filterClientsBySalespersonCodes(clients: Client[], codes: string[]): Client[] {
  const allowed = normalizeSalespersonCodes(codes);
  if (allowed.length === 0) return [];
  return clients.filter((c) => clientMatchesSalespersonCodes(c.owner_id, allowed));
}

/** Client ids whose owner_id (salesperson code) is in the actor's code set. */
function clientIdsMatchingSalespersonCodes(clients: Client[], codes: string[]): Set<string> {
  return new Set(filterClientsBySalespersonCodes(clients, codes).map((c) => String(c.id)));
}

function rowLinkedToSalespersonScopedAccount(
  row: { client_id?: string | null },
  scopedClientIds: Set<string>,
): boolean {
  if (row.client_id == null || row.client_id === '') return false;
  return scopedClientIds.has(String(row.client_id));
}

export function filterMyData(
  key: DataKey,
  data: CrmData,
  viewRole: UserRole,
  username: string,
  userFilter: UserFilter,
  teamUsernames: string[] = [],
  actorSalespersonCodes: string[] = [],
  codesByUsername: SalespersonCodesByUsername = {},
  departmentSlug: string | null = null,
): Client[] | Contact[] | Project[] | Task[] {
  const rows = data[key] as Array<{ owner_id?: string; client_id?: string | null }>;
  const me = data.users.find((u) => u.username === username);

  if (key === 'clients') {
    const clients = rows as Client[];
    // Management/IT/Admin/Customer Services/Finance (or elevated roles) see all;
    // Sales & Marketing (and others) only see assigned salesperson codes — empty codes ⇒ none.
    if (canViewAllAccounts(viewRole, departmentSlug)) {
      if (userFilter === 'All') return clients;
      const codes = codesForUserFilter(userFilter, username, actorSalespersonCodes, codesByUsername);
      return filterClientsBySalespersonCodes(clients, codes);
    }
    return filterClientsBySalespersonCodes(clients, actorSalespersonCodes);
  }

  // Full-account departments (and elevated roles): browse all related rows.
  if (canViewAllAccounts(viewRole, departmentSlug)) {
    if (userFilter === 'All') {
      return rows as never;
    }
    if (userFilter === 'Me') {
      return rows.filter((row) => ownerMatchesFilter(row.owner_id, username, data.users)) as never;
    }
    return rows.filter((row) => ownerMatchesFilter(row.owner_id, userFilter, data.users)) as never;
  }

  if (canViewAssignedTeam(viewRole)) {
    // Supervisor: salesperson-code accounts (all projects/tasks/contacts), plus own/team person rows.
    const scopedClientIds = clientIdsMatchingSalespersonCodes(data.clients, actorSalespersonCodes);
    const inSupervisorScope = (row: { owner_id?: string; client_id?: string | null }) =>
      ownerInAllowedSet(row.owner_id, username, data.users, teamUsernames) ||
      rowLinkedToSalespersonScopedAccount(row, scopedClientIds);

    if (userFilter === 'All') {
      return rows.filter((row) => inSupervisorScope(row)) as never;
    }

    if (userFilter === 'Me') {
      return rows.filter(
        (row) =>
          ownerMatchesFilter(row.owner_id, username, data.users) ||
          (me != null && row.owner_id === me.id) ||
          rowLinkedToSalespersonScopedAccount(row, scopedClientIds),
      ) as never;
    }

    const allowed = new Set(
      [username, ...teamUsernames].map((u) => u.trim().toLowerCase()).filter(Boolean),
    );
    const filterTarget = userFilter.trim().toLowerCase();
    if (!allowed.has(filterTarget)) {
      // Still allow account-scoped rows when filter is an unexpected value.
      return rows.filter((row) => rowLinkedToSalespersonScopedAccount(row, scopedClientIds)) as never;
    }
    return rows.filter(
      (row) =>
        ownerMatchesFilter(row.owner_id, userFilter, data.users) ||
        rowLinkedToSalespersonScopedAccount(row, scopedClientIds),
    ) as never;
  }

  // Regular user: contacts only via accounts matching their salesperson codes
  // (not person-owner — creating a contact must not grant list visibility).
  if (key === 'contacts') {
    const scopedClientIds = clientIdsMatchingSalespersonCodes(data.clients, actorSalespersonCodes);
    return rows.filter((row) => rowLinkedToSalespersonScopedAccount(row, scopedClientIds)) as never;
  }

  return rows.filter(
    (row) =>
      ownerMatchesFilter(row.owner_id, username, data.users) ||
      (me != null && row.owner_id === me.id),
  ) as never;
}

/** All accounts — for linking on tasks/projects (any sales user may tag any account). */
export function clientsForTaskProjectLinking(clients: Client[]): Client[] {
  return [...clients].sort((a, b) => a.company_name.localeCompare(b.company_name));
}

/** Accounts available in contact create & edit dropdowns. */
export function clientsForAccountPicker(
  clients: Client[],
  viewRole: UserRole,
  _username: string,
  _users: CrmUser[],
  _teamUsernames: string[] = [],
  actorSalespersonCodes: string[] = [],
  departmentSlug: string | null = null,
): Client[] {
  if (canViewAllAccounts(viewRole, departmentSlug)) {
    return clients;
  }
  return filterClientsBySalespersonCodes(clients, actorSalespersonCodes);
}

export type OwnedRecordAccessOptions = {
  departmentSlug?: string | null;
  clientId?: string | null;
  clients?: Client[];
  actorSalespersonCodes?: string[];
  /**
   * Contacts only: regular users may edit rows on accounts matching their salesperson codes.
   * Projects / tasks must stay owner-only for role=user (omit this flag).
   */
  userSalespersonScope?: boolean;
};

/** Whether user may view/edit a person-owned row (contacts / projects / tasks). */
export function userCanWriteOwnedRecord(
  ownerId: string | undefined,
  viewRole: UserRole,
  username: string,
  users: CrmUser[],
  teamUsernames: string[] = [],
  options: OwnedRecordAccessOptions = {},
): boolean {
  // Management/IT/Admin/Customer Services/Finance (or elevated roles): full access.
  if (canViewAllAccounts(viewRole, options.departmentSlug)) return true;

  const client =
    options.clientId && options.clients
      ? clientById(options.clients, options.clientId)
      : undefined;
  const onSalespersonAccount =
    Boolean(client) &&
    clientMatchesSalespersonCodes(client!.owner_id, options.actorSalespersonCodes ?? []);

  // Supervisor: salesperson-code accounts (all content), else team/own person rows.
  if (canViewAssignedTeam(viewRole)) {
    if (onSalespersonAccount) return true;
    return ownerInAllowedSet(ownerId, username, users, teamUsernames);
  }

  // Contacts: only accounts matching the user's salesperson codes (not person-owner alone).
  if (options.userSalespersonScope) {
    return onSalespersonAccount;
  }

  // Projects / tasks: person-owner only.
  return ownerMatchesFilter(ownerId, username, users);
}

/** Whether user may edit an account (clients.owner_id = salesperson code). */
export function userCanWriteClient(
  client: Client | undefined,
  viewRole: UserRole,
  actorSalespersonCodes: string[],
  departmentSlug: string | null = null,
): boolean {
  if (!client) return false;
  // Management/IT/Admin/Customer Services/Finance (or elevated roles): full account access.
  if (canViewAllAccounts(viewRole, departmentSlug)) return true;
  return clientMatchesSalespersonCodes(client.owner_id, actorSalespersonCodes);
}

/** Whether user may open account detail. */
export function userCanAccessClient(
  client: Client | undefined,
  viewRole: UserRole,
  _username: string,
  _users: CrmUser[],
  _teamUsernames: string[] = [],
  actorSalespersonCodes: string[] = [],
  departmentSlug: string | null = null,
): boolean {
  if (!client) return false;
  if (canViewAllAccounts(viewRole, departmentSlug)) {
    return true;
  }
  return clientMatchesSalespersonCodes(client.owner_id, actorSalespersonCodes);
}

export function accountTypeShortLabel(clientType?: string | null): string {
  if (!clientType?.trim()) return '—';
  const raw = clientType.trim();
  const lower = raw.toLowerCase();
  // IT Management statuses (exact match first).
  if (lower === 'active') return 'Active';
  if (lower === 'inactive') return 'Inactive';
  if (lower === 'blocked') return 'Blocked';
  if (lower === 'potential') return 'Potential';
  if (lower.includes('vip')) return 'VIP';
  if (lower.includes('potential')) return 'Potential';
  if (lower.includes('bad debt')) return 'Bad Debt';
  if (lower === 'closed' || lower.includes('closed')) return 'Closed';
  // Legacy bare "Customer" (and "Normal Customer") → Normal
  if (lower === 'customer' || lower.includes('normal') || lower.endsWith(' customer')) {
    return 'Normal';
  }
  return raw.replace(/\s+Customer$/i, '') || 'Normal';
}

export function accountTypeBadgeColor(clientType?: string): string {
  const kind = accountTypeShortLabel(clientType);
  if (kind === 'VIP' || kind === 'Blocked') return '#b71c1c';
  if (kind === 'Potential') return '#f1c40f';
  if (kind === 'Active') return '#15803d';
  if (kind === 'Inactive') return '#64748b';
  if (kind === 'Bad Debt') return '#5d4037';
  if (kind === 'Closed') return '#455a64';
  return '#7f8c8d';
}

/** Inline styles for type pills (Potential uses dark text on yellow). */
export function accountTypeBadgeStyle(clientType?: string): { backgroundColor: string; color: string } {
  const kind = accountTypeShortLabel(clientType);
  if (kind === 'VIP' || kind === 'Blocked') return { backgroundColor: '#b71c1c', color: '#fff' };
  if (kind === 'Potential') return { backgroundColor: '#f1c40f', color: '#1a1a1a' };
  if (kind === 'Active') return { backgroundColor: '#15803d', color: '#fff' };
  if (kind === 'Inactive') return { backgroundColor: '#64748b', color: '#fff' };
  if (kind === 'Bad Debt') return { backgroundColor: '#5d4037', color: '#fff' };
  if (kind === 'Closed') return { backgroundColor: '#455a64', color: '#fff' };
  return { backgroundColor: '#7f8c8d', color: '#fff' };
}

export { detectOwnerIdKind, formatClientSalespersonCode, clientMatchesSalespersonCodes };
